import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { create } from 'zustand';
import { supabase, checkIfBlocked } from '@/lib/supabase';
import { withTimeout, withTimeoutDefault } from '@/lib/withTimeout';
import type { User, Session } from '@supabase/supabase-js';
import type { Database } from '@/types/database.types';
import type { NavigateFunction } from 'react-router-dom';

type Profile = Database['public']['Tables']['profiles']['Row'];

// Constants
const AUTH_TIMEOUT = 5000; // 5 seconds for auth operations
const PROFILE_TIMEOUT = 5000; // 5 seconds for profile loading

interface AuthStore {
    // Core auth state
    user: User | null;
    profile: Profile | null;
    session: Session | null;

    // Loading states (separated for granular control)
    loading: boolean;           // Initial session check
    profileLoading: boolean;    // Profile data loading

    // Error states
    profileError: string | null;

    // Actions
    setUser: (user: User | null) => void;
    setProfile: (profile: Profile | null) => void;
    setSession: (session: Session | null) => void;
    setLoading: (loading: boolean) => void;
    setProfileLoading: (loading: boolean) => void;
    setProfileError: (error: string | null) => void;

    // Reset all state (for logout)
    reset: () => void;
}

const initialState = {
    user: null,
    profile: null,
    session: null,
    loading: true,
    profileLoading: false,
    profileError: null,
};

export const useAuthStore = create<AuthStore>((set) => ({
    ...initialState,
    setUser: (user) => set({ user }),
    setProfile: (profile) => set({ profile }),
    setSession: (session) => set({ session }),
    setLoading: (loading) => set({ loading }),
    setProfileLoading: (loading) => set({ profileLoading: loading }),
    setProfileError: (error) => set({ profileError: error }),
    reset: () => set(initialState),
}));

// Module-level profile loading — shared by AuthInitializer and useAuth actions
type ProfileSetters = {
    setProfile: (p: Profile | null) => void;
    setProfileLoading: (l: boolean) => void;
    setProfileError: (e: string | null) => void;
};

async function _loadProfile(
    userId: string,
    navigate: NavigateFunction,
    { setProfile, setProfileLoading, setProfileError }: ProfileSetters
): Promise<void> {
    setProfileLoading(true);
    setProfileError(null);

    try {
        // 1. Check if blocked (with timeout, default to not blocked)
        const bloqueado = await withTimeoutDefault(
            checkIfBlocked(userId),
            AUTH_TIMEOUT,
            false
        );

        if (bloqueado) {
            await supabase.auth.signOut();
            navigate('/login?blocked=true');
            return;
        }

        // 2. Load profile data with timeout
        const profileResult = await withTimeout(
            (async () => {
                return await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', userId)
                    .single();
            })(),
            PROFILE_TIMEOUT,
            'Timeout loading profile'
        );

        if (profileResult.error) {
            console.error('❌ Error loading profile:', profileResult.error);
            setProfileError('Erro ao carregar perfil');
            return;
        }

        setProfile(profileResult.data);
        console.log('✅ Profile loaded successfully');

    } catch (error: any) {
        console.error('❌ Profile load error:', error);
        setProfileError(error.message || 'Erro ao carregar perfil');
    } finally {
        setProfileLoading(false);
    }
}

// AuthInitializer — render once in App to install a single auth subscription
export function AuthInitializer() {
    const { setUser, setProfile, setSession, setLoading, setProfileLoading, setProfileError } = useAuthStore();
    const navigate = useNavigate();

    const loadProfile = useCallback((userId: string) =>
        _loadProfile(userId, navigate, { setProfile, setProfileLoading, setProfileError }),
        [navigate, setProfile, setProfileLoading, setProfileError]
    );

    useEffect(() => {
        let mounted = true;

        // 1. Check initial session
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (!mounted) return;

            setSession(session);
            setUser(session?.user ?? null);
            setLoading(false); // Auth check complete - UI can render

            // Load profile in background (non-blocking)
            if (session?.user) {
                loadProfile(session.user.id);
            }
        });

        // 2. Listen for auth changes (single subscription for the entire app)
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
                if (!mounted) return;

                console.log('🔄 Auth state change:', event);

                setSession(session);
                setUser(session?.user ?? null);

                if (session?.user) {
                    // Profile loading is NON-BLOCKING - don't await
                    loadProfile(session.user.id);
                } else {
                    setProfile(null);
                }

                // Always set loading false after auth change
                setLoading(false);
            }
        );

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, [loadProfile, setLoading, setProfile, setSession, setUser]);

    return null;
}

export function useAuth() {
    const {
        user, profile, session, loading, profileLoading, profileError,
        setProfile, setProfileLoading, setProfileError, reset
    } = useAuthStore();
    const navigate = useNavigate();

    const loadProfile = useCallback((userId: string) =>
        _loadProfile(userId, navigate, { setProfile, setProfileLoading, setProfileError }),
        [navigate, setProfile, setProfileLoading, setProfileError]
    );

    // Sign out - clear all state
    const signOut = useCallback(async () => {
        try {
            await supabase.auth.signOut();
        } catch (error) {
            console.warn('Sign out error (non-critical):', error);
        }
        reset();
        navigate('/login');
    }, [navigate, reset]);

    // Sign in function
    const signIn = useCallback(async (email: string, password: string) => {
        try {
            const { data, error } = await withTimeout(
                supabase.auth.signInWithPassword({ email, password }),
                AUTH_TIMEOUT,
                'Timeout during login'
            );

            if (error) throw error;

            // Check if blocked after login
            if (data.user) {
                const bloqueado = await withTimeoutDefault(
                    checkIfBlocked(data.user.id),
                    AUTH_TIMEOUT,
                    false
                );

                if (bloqueado) {
                    await signOut();
                    throw new Error('Acesso bloqueado. Contate o suporte.');
                }
            }

            return { data, error: null };
        } catch (error: any) {
            console.error('❌ Sign in error:', error);
            return { data: null, error };
        }
    }, [signOut]);

    // Update password function (used by TrocarSenha)
    const updatePassword = useCallback(async (newPassword: string) => {
        const { error } = await withTimeout(
            supabase.auth.updateUser({ password: newPassword }),
            AUTH_TIMEOUT,
            'Timeout updating password'
        );

        if (error) throw error;

        // Update primeiro_login flag
        if (user?.id) {
            await supabase
                .from('profiles')
                .update({ primeiro_login: false })
                .eq('id', user.id);

            // Reload profile to get updated data
            await loadProfile(user.id);
        }
    }, [loadProfile, user?.id]);

    // Reload profile manually (useful after changes)
    const reloadProfile = useCallback(() => {
        if (user?.id) {
            loadProfile(user.id);
        }
    }, [loadProfile, user?.id]);

    return {
        // State
        user,
        profile,
        session,
        loading,
        profileLoading,
        profileError,

        // Computed
        isAuthenticated: !!user,
        isPrimeiroLogin: profile?.primeiro_login ?? false,

        // Actions
        signIn,
        signOut,
        updatePassword,
        reloadProfile,
    };
}
